import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { ChecklistSubmissionInputBuilder } from "@nova/component-pilot-tracking-form"
import { FunctionComponent, useState } from "react"
import { ChecklistAnswers, type ChecklistForm as TChecklistForm } from "@nova/schemas"
import * as Icons from "@nova/component-icons"
import { ChecklistSubmissionWorkspaceVals } from "@nova/constants"
import { SelectOutlined } from "@nova/component-select"
import {
    CHECKLIST_LINKING_FORM,
    DEFAULT_VALID_FOR_HOURS,
    useChecklistFormControls,
    VALID_FOR_HOURS_OPTIONS,
} from "@nova/use-checklist-form"

type SubmitModeProps = {
    mode: "submit"
    onSubmit: (
        answers: ChecklistAnswers,
        linkingAnswers: ChecklistAnswers,
        validForHours: number
    ) => void
    submitting: boolean
}

type PreviewModeProps = {
    mode: "preview"
    onSubmit?: never
    submitting?: never
}

type ChecklistFormProps = {
    checklist: Pick<TChecklistForm, "name" | "description" | "form">
    workspaceVals: ChecklistSubmissionWorkspaceVals
    showLinkingForm: boolean
} & (SubmitModeProps | PreviewModeProps)

export const ChecklistForm: FunctionComponent<ChecklistFormProps> = ({
    checklist,
    mode,
    onSubmit,
    workspaceVals,
    showLinkingForm,
    submitting,
}) => {
    const { answers, canSubmit, answerControls, clearForm } = useChecklistFormControls(
        checklist.form
    )
    const {
        answers: linkingAnswers,
        canSubmit: canSubmitLinking,
        answerControls: linkingAnswerControls,
        clearForm: clearLinkingForm,
    } = useChecklistFormControls(CHECKLIST_LINKING_FORM)
    const [validForHours, setValidForHours] = useState(DEFAULT_VALID_FOR_HOURS)

    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2.5">
                <h1 className="text-white text-[28px] telegraf">{checklist.name}</h1>
                <div className="text-gray-100 text-[16px]">{checklist.description}</div>
            </div>
            <div className="w-full h-px bg-gray-700" />
            <div className="relative flex flex-col gap-8">
                {submitting && (
                    <div className="absolute inset-0">
                        {/* Block interaction while submitting */}
                    </div>
                )}
                {showLinkingForm && (
                    <>
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-1.5">
                                <small className="xs text-gray-400">Who&apos;s flying?</small>
                                <small className="xs text-gray-0">Valid for the next</small>
                            </div>
                            <div className="flex justify-between items-center gap-1.5">
                                <small className="text-gray-300">
                                    Your answers apply to matching flights in the next{" "}
                                    {validForHours} hours.
                                </small>
                                <SelectOutlined
                                    options={VALID_FOR_HOURS_OPTIONS}
                                    value={validForHours}
                                    onChange={setValidForHours}
                                />
                            </div>
                        </div>
                        {CHECKLIST_LINKING_FORM.map((question) => (
                            <ChecklistSubmissionInputBuilder
                                key={question.id}
                                questionSpec={question}
                                answerSpec={linkingAnswers[question.id]}
                                answerControls={linkingAnswerControls}
                                workspaceVals={workspaceVals}
                            />
                        ))}
                        <div className="w-full h-px bg-gray-700" />
                    </>
                )}
                {checklist.form.map((question) => (
                    <ChecklistSubmissionInputBuilder
                        key={question.id}
                        questionSpec={question}
                        answerSpec={answers[question.id]}
                        answerControls={answerControls}
                        workspaceVals={workspaceVals}
                    />
                ))}
            </div>
            <div className="w-full h-px bg-gray-700" />
            <div className="flex justify-between items-center gap-3 lg:gap-0">
                <ButtonTertiary
                    title="Clear Form"
                    LeftIcon={Icons.ClearAll}
                    onClick={() => {
                        clearForm()
                        clearLinkingForm()
                    }}
                    disabled={submitting}
                    className="h-12 flex-1 lg:h-auto lg:flex-none"
                />
                <ButtonPrimary
                    title="Submit Checklist"
                    RightIcon={Icons.Upload}
                    onClick={() =>
                        mode === "submit" && onSubmit(answers, linkingAnswers, validForHours)
                    }
                    disabled={
                        mode === "preview" ||
                        !canSubmit ||
                        (showLinkingForm && !canSubmitLinking) ||
                        submitting
                    }
                    className="h-12 px-4 flex-1 lg:h-auto lg:flex-none"
                />
            </div>
        </div>
    )
}
